import { AnimationsBehavior, BackgroundMedia, BackgroundOverlayFillLayerCSSVars } from '@wix/thunderbolt-becky-types'
import { CompInfo } from '@wix/thunderbolt-components-native'
import { isMediaFullScreenByEffect, getMediaType } from '../Media/Media.utils'
import { color as _colorApi } from '@wix/thunderbolt-commons'

const { extractThemeColor, getRGBAColorString, isThemeColor, isValidColor } = _colorApi

const hasImageOverlay = (backgroundMedia: BackgroundMedia): boolean => !!backgroundMedia.imageOverlay

const hasColorOverlay = (backgroundMedia: BackgroundMedia): boolean => backgroundMedia.colorOverlay !== ''

const mediaHasOverlay = (backgroundMedia: BackgroundMedia): boolean => {
	const mediaType = getMediaType(backgroundMedia)
	const showOverlayForMediaType = backgroundMedia.showOverlayForMediaType || 'WixVideo'

	const shouldRenderOverlay = showOverlayForMediaType === 'all' || showOverlayForMediaType === mediaType
	const hasOverlay = hasImageOverlay(backgroundMedia) || hasColorOverlay(backgroundMedia)
	return hasOverlay && shouldRenderOverlay
}

export const parseColorToRGBA = (color: string = 'none', opacity: number = 1) => {
	const parsedColor = extractThemeColor(color)
	const isValid = isValidColor(parsedColor)

	const isTransparent = !isValid || color === 'none' || color === 'noColor' || opacity === 0
	if (isTransparent) {
		return 'transparent'
	}
	if (isThemeColor(parsedColor)) {
		return opacity === 1 ? `rgb(var(--${parsedColor}))` : `rgba(var(--${parsedColor}), ${opacity})`
	}
	if (parsedColor === 'color_undefined') {
		return '#FFFFFF'
	}
	return getRGBAColorString(color, opacity)
}

export const getBackgroundOverlayFillLayerCSSVars = ({
	animations,
	viewMode,
	backgroundMedia,
}: Pick<CompInfo, 'experiments' | 'isMobileView' | 'viewMode'> & {
	backgroundMedia: BackgroundMedia
	isNonAdaptiveMobile: boolean
	animations: Array<AnimationsBehavior>
}): Partial<BackgroundOverlayFillLayerCSSVars> | undefined => {
	const hasOverlay = mediaHasOverlay(backgroundMedia)
	if (!hasOverlay) {
		return
	}
	const bgOverlayColor = hasColorOverlay(backgroundMedia)
		? parseColorToRGBA(backgroundMedia.colorOverlay, backgroundMedia.colorOverlayOpacity)
		: 'transparent'
	const isFullScreenByEffect = isMediaFullScreenByEffect({
		animations,
		viewMode,
	})
	const blendMode = backgroundMedia.overlayBlending?.blendMode
	const blendOpacity = backgroundMedia.overlayBlending?.opacity
	const noBlendOpacityFallBack = blendOpacity && (blendMode === 'overlay' ? blendOpacity / 2 : blendOpacity)
	const backgroundOverlayCSSVars = {
		'--fill-layer-background-overlay-color': bgOverlayColor,
		'--fill-layer-background-overlay-position': isFullScreenByEffect ? ('fixed' as const) : ('absolute' as const),
		...(blendMode && { '--fill-layer-background-overlay-blend-mode': blendMode }),
		...(blendOpacity && { '--fill-layer-background-overlay-blend-opacity': blendOpacity }),
		...(noBlendOpacityFallBack && {
			'--fill-layer-background-overlay-blend-opacity-fallback': noBlendOpacityFallBack,
		}),
	}

	return backgroundOverlayCSSVars
}
